import { Box, Divider, Typography } from '@mui/material';
import { rootSx } from './style';

export default function SupervisedGuideContent() {
  return (
    <Box className="SupervisedGuideContent-root" sx={rootSx}>
      <Box className="SupervisedGuideContent-main">
        <Typography className="SupervisedGuideContent-title">지도학습 설명</Typography>
        <Typography className="SupervisedGuideContent-subtitle">
          함수를 유추해내기 위한 기계 학습(Machine Learning)의 한 방법
        </Typography>
      </Box>

      <Box className="SupervisedGuideContent-section">
        <Box className="SupervisedGuideContent-imageBox">
          <img src="/_static/images/supervised-guide_1.png" width="100%" alt="" />
        </Box>
        <Typography>
          <b>지도 학습(Supervised Learning)</b>은 컴퓨터에게 정답(Label)이 무엇인지 알려주면서
          컴퓨터를 학습시키는 방법입니다. <br />
        </Typography>
      </Box>

      <Divider />
      <Box className="SupervisedGuideContent-section">
        <Box className="SupervisedGuideContent-title">분류 모델</Box>
        <Typography>
          인터넷에는 사진을 보고 강아지인지 고양이인지를 판단하는 인공 지능이 있습니다. 이 인공
          지능은 어떻게 만들었을까요? 사람은 사진을 보자마자 강아지인지 고양이 인지 알 수 있지만,
          컴퓨터는 그럴 수 없습니다. 많은 사진으로 컴퓨터에게 이것은 고양이, 이것은 강아지라고
          사전에 가르쳐주어야 합니다.
          <Box className="SupervisedGuideContent-imageBox">
            <img src="/_static/images/supervised-guide_2.jpg" width="100%" alt="" />
          </Box>
        </Typography>
        <Box className="SupervisedGuideContent-subtitle">분류란</Box>
        <Typography>
          인공 지능이 고양이와 강아지로 판단한다는 말은 고양이와 강아지로 <em>분류</em>한다고
          표현하기도 합니다. 이제부터는 인공 지능이 판단한다고 표현하지 않고, <em>분류</em>한다고
          표현하겠습니다. 영어로는 <b>classification</b> 입니다.
        </Typography>
        <Box className="SupervisedGuideContent-subtitle">학습과 학습자료는 무엇일까요</Box>
        <Typography>
          <em>분류</em>하기 위해 컴퓨터에게 가르쳐주어야 한다고 했습니다. 이렇게 컴퓨터에게
          가르쳐주는 것을 <em>학습</em>이라고 표현합니다. 여러분도 교실에서 학습하고 있지요?
          컴퓨터도 학습을 해야 인공 지능이 될 수 있습니다.
        </Typography>
        <Typography>
          그리고 학습할 때 사용한 자료들을 <em>학습 자료</em>라고 표현합니다. 강아지와 고양이
          사진들이 학습 자료입니다. 어떤 사람은 학습 자료를 <em>학습 데이터</em>라고 부릅니다.
          강아지와 고양이 사진들은 컴퓨터의 입장에서는 데이터일 뿐이라서 학습 자료와 학습 데이터는
          같은 말이라고 할 수 있습니다. 사람은 책을 통해서 학습하지만, 컴퓨터는 데이터를 통해서
          학습합니다.
        </Typography>
        <Box className="SupervisedGuideContent-subtitle">
          이미지 외에 다른 것은 분류할 수 없나요
        </Box>
        <Typography>
          컴퓨터는 데이터로 표현할 수 있는 모든 것을 분류할 수 있습니다. <em>이미지</em>를 분류할
          수도 있고,
          <em>텍스트</em>도 분류할 수 있습니다. 심지어 <em>소리</em>도 분류할 수 있습니다. 간단한
          소리 분류는 여자/남자 목소리를 분류하는 것입니다. 하지만 소리 분류는 배워야 할 지식이 많기
          때문에 조금 어렵습니다. 처음에는 텍스트와 이미지 분류를 먼저 도전하시길 추천드립니다.
        </Typography>
        <Typography>
          혹시 비디오 분류는 없는지 궁금할 수도 있겠습니다. 비디오는 많은 이미지를 빠르게 표시하는
          방식이라서, 기본적으로 이미지 분류입니다. 비디오의 소리 부분은 소리 분류를 이용합니다.
          비디오를 분류할 때는 이미지와 소리를 따로 따로 분리해서 분류하므로 별도로 비디오 분류는
          없는 것입니다.
        </Typography>
      </Box>

      <Divider />
      <Box className="SupervisedGuideContent-section">
        <Box className="SupervisedGuideContent-title">텍스트 분류</Box>
        <Typography>
          텍스트 분류의 대표적인 예는 스팸 메일 필터입니다. 스팸 메일 필터는 메일의 내용을 분석하여
          스팸인지 아닌지 분류합니다. 메일의 내용은 텍스트이므로 텍스트 분류에 해당됩니다.
          <Box className="SupervisedGuideContent-imageBox">
            <img src="/_static/images/supervised-guide_3.png" width={300} alt="" />
          </Box>
        </Typography>
        <Box className="SupervisedGuideContent-title">이미지 분류</Box>
        <Typography>
          이미지 분류의 대표적인 예는 손글씨 인식입니다. 우리가 종이에 쓴 글자가 어떤 글자인지를
          분류합니다. 아래 예는 손으로 쓴 숫자를 이미지로 만들어, 0에서 9까지의 숫자로 분류하고
          있습니다.
          <Box className="SupervisedGuideContent-imageBox">
            <img src="/_static/images/supervised-guide_4.png" width={300} alt="" />
          </Box>
        </Typography>
        <Typography>
          스팸 메일 필터는 스팸인지 아닌지를 분류하는데, 이렇게 둘 중 하나로 분류하는 것을 특별히{' '}
          <em>이항 분류</em>라고 부릅니다. 손글씨에서는 0에서 9까지의 숫자 10개로 분류하고 있습니다.
          이렇게 3개 이상으로 분류하는 것을 <em>다항 분류</em>라고 부릅니다.
        </Typography>
        <Typography>
          이항 분류, 다항 분류는 데이터 과학자들이 사용하는 용어입니다. 데이터 과학자들은 이항
          분류와 다항 분류에 다른 계산식을 사용하기 때문에 구분하고 있습니다. 이제 막 입문하신
          분들은 복잡한 수식보다는 용어만 기억하고 있어도 좋을 것 같습니다.
        </Typography>
      </Box>
    </Box>
  );
}
